import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { DeepCopyPipe } from '@quorum/common-pipes';
import { ResponseInterceptorOptions as ApiResponseHeaders } from '@quorum/models/xs-misc';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export const API_BASE = new InjectionToken('API_BASE');
export const AUTH_API_BASE = new InjectionToken('AUTH_API_BASE');
export const LAMBDA_API_BASE = new InjectionToken('LAMBDA_API_BASE ');
export const LEGACY_API_BASE = new InjectionToken('LEGACY_API_BASE');
export const LEGACY_AUTH_BASE = new InjectionToken('LEGACY_AUTH_BASE');
export const V4_API_BASE = new InjectionToken<string>('V4_API_BASE');

// URL path prefixes (the portion after `v/<n>/`) routed through
// v4-staging-api / v4-api. A request is sent to the v4 host when its path,
// after the version segment, equals one of these entries or continues with
// `/` or `?` (see `resolveBase`). Paths are otherwise unchanged.
//
// Most backend domains own their first URL segment, so a single coarse entry
// covers them — e.g. `'associates'` routes every `v/1/associates/**` call.
// A domain whose URLs are nested under a *shared* parent must list its
// specific resource roots instead of the bare parent, so sibling domains
// under that parent stay on v1. e.g. service-quotes lives under the shared
// `fixed-operations` parent (alongside work-orders, parts, warranty, …), so
// it is listed as `'fixed-operations/quote-headers'`,
// `'fixed-operations/quote-details'`, … — never `'fixed-operations'`, which
// would drag every sibling onto v4. The exact roots a domain owns are derived
// from its server routes by `ai/scripts/lib/frontend-prefixes.ts`.
//
// When adding entries for a domain you must also add a matching Cypress spec
// at `apps/v4-migration-e2e/src/integration/<domain>.spec.ts`. The CI workflow
// (.github/workflows/v4-migration-e2e.yml) runs a pre-flight check
// (`apps/v4-migration-e2e/scripts/check-spec-coverage.ts`) that fails the
// build if a migrated domain has no matching spec file, then runs the
// suite. See `apps/v4-migration-e2e/README.md` for the template.
export const V4_DOMAINS: ReadonlySet<string> = new Set<string>([
  'associates',
  'crm',
  // service-quotes — URLs are nested under the shared `fixed-operations` parent
  // (alongside work-orders, parts, warranty, …), so it is listed by its
  // resource roots, never the bare `fixed-operations` segment.
  'fixed-operations/quote-additional-charges',
  'fixed-operations/quote-details',
  'fixed-operations/quote-headers',
  'fixed-operations/quote-sales-additional-charges',
  'fixed-operations/quote-sales-details',
  'fixed-operations/quote-statuses',
  // service-appointments — like service-quotes, most URLs are nested under the
  // shared `fixed-operations` parent, so each resource root is listed
  // individually; the bare `fixed-operations` segment is never added. The flat
  // `service-appointments` root is the top-level prefix for the domain itself.
  'fixed-operations/appointment-cases',
  'fixed-operations/appointment-parts',
  'fixed-operations/appointment-sources',
  'fixed-operations/appointment-statuses',
  'fixed-operations/appointments',
  'fixed-operations/appointments-integration-data',
  'fixed-operations/gm-slt',
  'fixed-operations/service-appointment-declined-mpvi-codes',
  'fixed-operations/service-visits',
  'fixed-operations/workflow/gm-slt',
  'service-appointments',
  // work-orders — nested under the shared `fixed-operations` parent, so each
  // resource root is listed individually; the bare `fixed-operations` segment is
  // never added. The labour-*, vehicle-inspection-*, and menu-pricing roots are
  // sub-modules physically owned by the `server/api/work-orders/` backend domain
  // folder (uniquely — no sibling domain declares them), so they cut over to v4
  // with work-orders even though their URL names do not carry the `work-order`
  // prefix. The lone work-orders route under the appointments resource
  // (`appointments/:id/work-order-events`) is already covered by the
  // `fixed-operations/appointments` entry above, so it is not repeated here.
  'fixed-operations/labour-matrix-pricings',
  'fixed-operations/labour-price-calculations',
  'fixed-operations/labour-rate-bases',
  'fixed-operations/menu-pricing/jobs',
  'fixed-operations/menu-pricing/workflow/jobs',
  'fixed-operations/vehicle-inspection-categories',
  'fixed-operations/vehicle-inspection-tasks',
  'fixed-operations/vehicle-inspections',
  'fixed-operations/work-order',
  'fixed-operations/work-order-additional-charges',
  'fixed-operations/work-order-cases',
  'fixed-operations/work-order-cashiers',
  'fixed-operations/work-order-declined-mpvi-codes',
  'fixed-operations/work-order-events',
  'fixed-operations/work-order-labour-op-codes',
  'fixed-operations/work-order-labour-op-narratives',
  'fixed-operations/work-order-labour-rate-basis-oem-mappings',
  'fixed-operations/work-order-labours',
  'fixed-operations/work-order-mechanics-hours',
  'fixed-operations/work-order-priority-numbers',
  'fixed-operations/work-order-request-sub-labour-ops',
  'fixed-operations/work-order-request-subs',
  'fixed-operations/work-order-supplemental-hours',
  'fixed-operations/work-orders',
  'fixed-operations/work-orders-integration-data',
  // vehicles — the vehicles, singular vehicle, and shared fixed-operations first
  // segments are each shared with sibling domains, so the domain is listed by its
  // specific resource roots (derived from its server routes) rather than a bare
  // segment; a coarse vehicles/vehicle entry would over-route siblings onto v4.
  'fixed-operations/service-history-dashboard-cache-times',
  'fixed-operations/vehicle-ivh',
  'vehicle/vehicle-other-extended-warranties',
  'vehicle/vehicle-template-detail',
  'vehicles/ford-vehicle-service-histories',
  'vehicles/fuel-units-of-measure',
  'vehicles/generic-models',
  'vehicles/gm-service-histories',
  'vehicles/nissan-vehicle-service-history',
  'vehicles/service-history-dashboards',
  'vehicles/toyota-model-colour-options',
  'vehicles/toyota-models',
  'vehicles/toyota-vin-download',
  'vehicles/units-of-measure',
  'vehicles/vehicle-bodies',
  'vehicles/vehicle-categories',
  'vehicles/vehicle-codes',
  'vehicles/vehicle-colours',
  'vehicles/vehicle-employee-associations',
  'vehicles/vehicle-groups',
  'vehicles/vehicle-inspection-wear-patterns',
  'vehicles/vehicle-invoices',
  'vehicles/vehicle-maintenance-future-services',
  'vehicles/vehicle-maintenance-schedules',
  'vehicles/vehicle-makes',
  'vehicles/vehicle-misc-charge-types',
  'vehicles/vehicle-misc-charges',
  'vehicles/vehicle-model-codes-dfs-report-lists',
  'vehicles/vehicle-other-extended-warranties',
  'vehicles/vehicle-pricing-types',
  'vehicles/vehicle-prospect-types',
  'vehicles/vehicle-receipt-methods',
  'vehicles/vehicle-statuses',
  'vehicles/vehicle-sub-categories',
  'vehicles/vehicle-sub-types',
  'vehicles/vehicle-template-service-types',
  'vehicles/vehicle-templates',
  'vehicles/vehicle-types',
  'vehicles/vehicles',
  'vehicles/vin-decode',
  'vehicles/vin-translate',
  'system',
  // fixed-operations-common — nested under the shared `fixed-operations` parent,
  // so each resource root is listed individually; the bare `fixed-operations`
  // segment is never added. The one route this domain declares under the
  // work-orders resource (`work-orders/:workOrderId/mechanics-hours-actuals`) is
  // covered by the `fixed-operations/work-orders` entry above, and the
  // `service-groups/workflows/service-group-price` workflow root is covered by
  // `fixed-operations/service-groups`, so neither is repeated here.
  'fixed-operations/action-types',
  'fixed-operations/business-units',
  'fixed-operations/calculation-transactions',
  'fixed-operations/campaign-headers',
  'fixed-operations/claim-continuations',
  'fixed-operations/claim-submission-types',
  'fixed-operations/colours',
  'fixed-operations/complaint-codes',
  'fixed-operations/complaint-types',
  'fixed-operations/declined-reasons',
  'fixed-operations/declined-services',
  'fixed-operations/declined-services-tasks',
  'fixed-operations/diagnostic-trouble-codes',
  'fixed-operations/failure-codes',
  'fixed-operations/flash-product-reports',
  'fixed-operations/message-transaction-message-preferences',
  'fixed-operations/motor/dealer-maintenance-recommendations',
  'fixed-operations/motor/op-code-mappings',
  'fixed-operations/oem-service-group-rates',
  'fixed-operations/process-flash-product-report',
  'fixed-operations/report-clause-maintenances',
  'fixed-operations/report-clause-types',
  'fixed-operations/ro-sync-item-identifier-mappings',
  'fixed-operations/service-codes',
  'fixed-operations/service-group-categories',
  'fixed-operations/service-group-complaints',
  'fixed-operations/service-group-rates',
  'fixed-operations/service-groups',
  'fixed-operations/service-groups-mappings',
  'fixed-operations/service-notices',
  'fixed-operations/services',
  'fixed-operations/third-party-warranty-pst-taxes',
  'fixed-operations/toyota-campaign-refusal-codes',
  'fixed-operations/toyota-t1-code-categories',
  'fixed-operations/toyota-t1-codes',
  'fixed-operations/toyota-t2-code-categories',
  'fixed-operations/toyota-t2-codes',
  'fixed-operations/work-order-mechanics-hours-actuals',
]);

@Injectable()
export class ApiService {
  constructor(
    private http: HttpClient,
    @Inject(API_BASE) public apiBase: string,
    @Inject(AUTH_API_BASE) public authApiBase: string,
    @Inject(LAMBDA_API_BASE) public lambdaApiBase: string,
    @Inject(LEGACY_API_BASE) private legacyApiBase: string,
    @Inject(LEGACY_AUTH_BASE) private legacyAuthBase: string,
    @Optional() @Inject(V4_API_BASE) private v4ApiBase: string | null = null
  ) {}

  private resolveBase(path: string): string {
    const versioned = stripSlash(path).match(/^v\/\d+\/(.*)$/);
    if (versioned && this.v4ApiBase) {
      const rest = versioned[1];
      for (const prefix of V4_DOMAINS) {
        if (rest === prefix || rest.startsWith(`${prefix}/`) || rest.startsWith(`${prefix}?`)) {
          return this.v4ApiBase;
        }
      }
    }
    return this.apiBase;
  }

  getWithHeaders<T>(path: string, opts?: any): Observable<any> {
    path = stripSlash(path);

    if (opts) {
      return this.http.get(`${this.resolveBase(path)}/${path}`, { ...opts, observe: 'response' }).pipe(
        map((res: any) => {
          const headers = new ApiResponseHeaders({
            etag: res.headers.get('etag') ? res.headers.get('etag') : null,
            total: res.headers.get('x-total') ? parseInt(res.headers.get('x-total')) : null,
            totalPages: res.headers.get('x-total-pages') ? parseInt(res.headers.get('x-total-pages')) : null,
          });
          return { body: res.body, headers: headers };
        }),
        catchError((err: Response) => catcher(err, path))
      );
    } else {
      return this.http.get(`${this.resolveBase(path)}/${path}`, { observe: 'response' }).pipe(
        map((res) => {
          const headers = new ApiResponseHeaders({
            etag: res.headers.get('etag') ? res.headers.get('etag') : null,
            total: res.headers.get('x-total') ? parseInt(res.headers.get('x-total')) : null,
            totalPages: res.headers.get('x-total-pages') ? parseInt(res.headers.get('x-total-pages')) : null,
          });
          return { body: res.body, headers: headers };
        }),
        catchError((err: Response) => catcher(err, path))
      );
    }
  }

  get<T>(path: string, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.get(`${this.resolveBase(path)}/${path}`, opts).pipe(
        map((res) => {
          return res;
        }),
        catchError((err: Response) => {
          return catcher(err, path);
        })
      );
    } else {
      return this.http.get(`${this.resolveBase(path)}/${path}`).pipe(
        map((res) => res),
        catchError((err: Response) => catcher(err, path))
      );
    }
  }

  head<T>(path: string, opts?: any): Observable<T> {
    path = stripSlash(path);
    opts = Object.assign({}, { observe: 'response', responseType: 'json' }, opts);
    return this.http.head(`${this.resolveBase(path)}/${path}`, opts).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((err: Response) => catcher(err, path))
    );
  }

  patch<T>(path: string, body: any, opts?: any): Observable<any> {
    path = stripSlash(path);
    return this.http.patch(`${this.resolveBase(path)}/${path}`, body).pipe(
      map((res) => res),
      catchError((err) => catcher(err, path))
    );
  }

  put<T>(path: string, body: any, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.put(`${this.resolveBase(path)}/${path}`, body ? emptyStringsToNull(body) : undefined, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.put(`${this.resolveBase(path)}/${path}`, body ? emptyStringsToNull(body) : undefined).pipe(
        map((res: any) => {
          return res;
        }),
        catchError((err) => catcher(err, path))
      );
    }
  }

  putWithHeaders<T>(path: string, body: any, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http
        .put(`${this.resolveBase(path)}/${path}`, body ? emptyStringsToNull(body) : undefined, {
          ...opts,
          observe: 'response',
        })
        .pipe(
          map((res: any) => {
            const headers = new ApiResponseHeaders({});
            return { body: res.body, headers: headers };
          }),
          catchError((err) => catcher(err, path))
        );
    } else {
      return this.http
        .put(`${this.resolveBase(path)}/${path}`, body ? emptyStringsToNull(body) : undefined, { observe: 'response' })
        .pipe(
          map((res: any) => {
            const headers = new ApiResponseHeaders({});

            return { body: res, headers: headers };
          }),
          catchError((err) => catcher(err, path))
        );
    }
  }

  post<T>(path: string, body: any, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http
        .post(`${this.resolveBase(path)}/${path}`, body ? emptyStringsToNull(body) : undefined, opts)
        .pipe(
          map((res: any) => {
            if (opts.observe) return res.body;
            else return res;
          }),
          catchError((err) => catcher(err, path))
        );
    } else {
      return this.http.post(`${this.resolveBase(path)}/${path}`, body ? emptyStringsToNull(body) : undefined).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }

  delete<T>(path: string, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.delete(`${this.resolveBase(path)}/${path}`, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.delete(`${this.resolveBase(path)}/${path}`).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }

  // Lambda API Implementation START
  lambdaGet<T>(path: string, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.get(`${this.lambdaApiBase}/${path}`, opts).pipe(
        map((res) => res),
        catchError((err: Response) => catcher(err, path))
      );
    } else {
      return this.http.get(`${this.lambdaApiBase}/${path}`).pipe(
        map((res) => res),
        catchError((err: Response) => catcher(err, path))
      );
    }
  }

  lambdaGetWithHeaders<T>(path: string, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.get(`${this.lambdaApiBase}/${path}`, { ...opts, observe: 'response' }).pipe(
        map((res: any) => {
          const headers = new ApiResponseHeaders({
            etag: res.headers.get('etag') ? res.headers.get('etag') : null,
            total: res.headers.get('x-total') ? parseInt(res.headers.get('x-total')) : null,
            totalPages: res.headers.get('x-total-pages') ? parseInt(res.headers.get('x-total-pages')) : null,
          });
          return { body: res.body, headers: headers };
        }),
        catchError((err: Response) => catcher(err, path))
      );
    } else {
      return this.http.get(`${this.lambdaApiBase}/${path}`, { observe: 'response' }).pipe(
        map((res) => {
          const headers = new ApiResponseHeaders({
            etag: res.headers.get('etag') ? res.headers.get('etag') : null,
            total: res.headers.get('x-total') ? parseInt(res.headers.get('x-total')) : null,
            totalPages: res.headers.get('x-total-pages') ? parseInt(res.headers.get('x-total-pages')) : null,
          });
          return { body: res.body, headers: headers };
        }),
        catchError((err: Response) => catcher(err, path))
      );
    }
  }

  lambdaPost<T>(path: string, body: any, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.post(`${this.lambdaApiBase}/${path}`, body ? emptyStringsToNull(body) : undefined, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.post(`${this.lambdaApiBase}/${path}`, body ? emptyStringsToNull(body) : undefined).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }

  lambdaPut<T>(path: string, body: any, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.put(`${this.lambdaApiBase}/${path}`, body ? emptyStringsToNull(body) : undefined, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.put(`${this.lambdaApiBase}/${path}`, body ? emptyStringsToNull(body) : undefined).pipe(
        map((res: any) => {
          return res;
        }),
        catchError((err) => catcher(err, path))
      );
    }
  }

  lambdaPutWithHeaders<T>(path: string, body: any, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http
        .put(`${this.lambdaApiBase}/${path}`, body ? emptyStringsToNull(body) : undefined, {
          ...opts,
          observe: 'response',
        })
        .pipe(
          map((res: any) => {
            const headers = new ApiResponseHeaders({});
            return { body: res.body, headers: headers };
          }),
          catchError((err) => catcher(err, path))
        );
    } else {
      return this.http
        .put(`${this.lambdaApiBase}/${path}`, body ? emptyStringsToNull(body) : undefined, { observe: 'response' })
        .pipe(
          map((res: any) => {
            const headers = new ApiResponseHeaders({});
            return { body: res, headers: headers };
          }),
          catchError((err) => catcher(err, path))
        );
    }
  }

  lambdaPatch<T>(path: string, body: any, opts?: any): Observable<any> {
    path = stripSlash(path);
    return this.http.patch(`${this.lambdaApiBase}/${path}`, body).pipe(
      map((res) => res),
      catchError((err) => catcher(err, path))
    );
  }

  lambdaDelete<T>(path: string, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.delete(`${this.lambdaApiBase}/${path}`, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.delete(`${this.lambdaApiBase}/${path}`).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }
  // Lambda API Implementation END

  // New Cognito Auth API Methods START
  getCognitoAuth<T>(path: string, opts?: any): Observable<any> {
    path = stripSlash(path);
    if (opts) {
      return this.http.get(`${this.authApiBase}/${path}`, opts).pipe(
        map((res) => {
          return res;
        }),
        catchError((err: Response) => {
          return catcher(err, path);
        })
      );
    } else {
      return this.http.get(`${this.authApiBase}/${path}`).pipe(
        map((res) => res),
        catchError((err: Response) => catcher(err, path))
      );
    }
  }

  postCognitoAuth<T>(path: string, body: any, opts?: any): Observable<any> {
    if (opts) {
      return this.http.post(`${this.authApiBase}/${path}`, body ? emptyStringsToNull(body) : undefined, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.post(`${this.authApiBase}/${path}`, body ? emptyStringsToNull(body) : undefined).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }
  // New Cognito Auth API Methods END

  // Legacy API Methods START
  legacyPost<T>(path: string, body: any, opts?: any): Observable<any> {
    if (opts) {
      return this.http.post(`${this.legacyApiBase}/${path}`, body, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.post(`${this.legacyApiBase}/${path}`, body).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }

  legacyPut<T>(path: string, body: any, opts?: any): Observable<any> {
    if (opts) {
      return this.http.put(`${this.legacyApiBase}/${path}`, body, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.put(`${this.legacyApiBase}/${path}`, body).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }

  legacyGet<T>(path: string, opts?: any): Observable<any> {
    if (opts) {
      return this.http.get(`${this.legacyApiBase}/${path}`, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.get(`${this.legacyApiBase}/${path}`).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }

  legacyDelete<T>(path: string, opts?: any): Observable<any> {
    if (opts) {
      return this.http.delete(`${this.legacyApiBase}/${path}`, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.delete(`${this.legacyApiBase}/${path}`).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }

  legacyAuthGet<T>(path: string, opts?: any): Observable<any> {
    if (opts) {
      return this.http.get(`${this.legacyAuthBase}/${path}`, opts).pipe(
        map((res: any) => {
          if (opts.observe) return res.body;
          else return res;
        }),
        catchError((err) => catcher(err, path))
      );
    } else {
      return this.http.get(`${this.legacyAuthBase}/${path}`).pipe(
        map((res) => res),
        catchError((err) => catcher(err, path))
      );
    }
  }
  // Legacy API Methods END

  // Helper functions START
  getApiUrl() {
    return this.apiBase;
  }
  // Helper functions END
}

function catcher(err: any, path: string): any {
  if (err && (err.name === 'TimeoutError' || err.status === 0)) {
    return throwError({
      reason: `Network connection lost. Please check your WiFi and try again.`,
      statusCode: 0,
    });
  }
  switch (err.status) {
    case 403:
      return throwError({
        reason: `The requested operation is forbidden.`,
        statusCode: 403,
      });
    case 404:
      return throwError({
        reason: `Data for /${path} could not be loaded at this time.`,
        statusCode: 404,
      });
    case 500:
      return throwError({
        reason: `Internal server error. ${err.error.error_description}`,
        statusCode: 500,
      });
    case 503: {
      const body: any = err.error;
      return throwError({
        reason: body.error_description,
        statusCode: 503,
      });
    }
    case 422: {
      // v4 answers `{ message }`; the legacy API sends the message as a bare string.
      const body: unknown = err.error;
      const message = typeof body === 'string' ? body : (body as { message?: unknown } | null)?.message;
      return throwError({
        reason: typeof message === 'string' && message.trim() !== '' ? message : `Unknown error loading /${path}.`,
        statusCode: 422,
      });
    }
    default:
      return throwError({
        reason: `Unknown error loading /${path}.`,
        statusCode: err.status,
      });
  }
}

/**
 * Fix path if it has a leading slash.
 */
function stripSlash(path: string): string {
  return path.replace(/^\//, '');
}

function emptyStringsToNull(body: any) {
  const bodyCopy = new DeepCopyPipe().transform(body);
  Object.keys(bodyCopy).forEach((entry: any) => {
    if (bodyCopy[entry] === '') {
      bodyCopy[entry] = null;
    }
  });
  return bodyCopy;
}
